import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { useAuth, api } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Bot, Save, CreditCard, AlertCircle, User as UserIcon, Trophy } from "lucide-react";

export default function Settings() {
    const { t } = useLang();
    const { user, plan, updateSettings } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [nameSaving, setNameSaving] = useState(false);
    const [aiName, setAiName] = useState(user?.ai_name || "IA");
    const [saving, setSaving] = useState(false);
    const [leaderboardOptIn, setLeaderboardOptIn] = useState(user?.leaderboard_opt_in !== false);
    const [leaderboardSaving, setLeaderboardSaving] = useState(false);

    const [sub, setSub] = useState(null);
    const [subLoading, setSubLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const isPaidPlan = plan && plan !== "free" && plan !== "studio_member";

    useEffect(() => {
        if (!isPaidPlan) {
            setSubLoading(false);
            return;
        }
        api.get("/payments/subscription")
            .then((r) => setSub(r.data))
            .catch(() => setSub(null))
            .finally(() => setSubLoading(false));
    }, [isPaidPlan]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await updateSettings({ ai_name: aiName.trim() || "IA" });
            toast.success(t("settings.saved"));
        } catch (err) {
            toast.error(err.response?.data?.detail || t("common.error"));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveName = async (e) => {
        e.preventDefault();
        setNameSaving(true);
        try {
            await updateSettings({ name: name.trim() });
            toast.success(t("settings.saved"));
        } catch (err) {
            toast.error(err.response?.data?.detail || t("common.error"));
        } finally {
            setNameSaving(false);
        }
    };

    const handleToggleLeaderboard = async (checked) => {
        setLeaderboardOptIn(checked);
        setLeaderboardSaving(true);
        try {
            await updateSettings({ leaderboard_opt_in: checked });
        } catch (err) {
            setLeaderboardOptIn(!checked);
            toast.error(err.response?.data?.detail || t("common.error"));
        } finally {
            setLeaderboardSaving(false);
        }
    };

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await api.post("/payments/cancel-subscription");
            toast.success(t("settings.sub_cancel_success"));
            setSub((s) => ({ ...s, cancel_at_period_end: true }));
        } catch (err) {
            toast.error(err.response?.data?.detail || t("common.error"));
        } finally {
            setCancelling(false);
        }
    };

    return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
                <div className="mono-label mb-3">/ SETTINGS</div>
                <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-12" data-testid="settings-title">
                    {t("settings.title")}
                </h1>

                <form onSubmit={handleSaveName} className="card-surface p-8 mb-8" data-testid="account-name-form">
                    <div className="mono-label mb-2 flex items-center gap-2">
                        <UserIcon size={14} className="text-brand" /> {t("settings.account_identity")}
                    </div>
                    <label className="block text-sm text-slate-600 mb-2" htmlFor="account-name-input">
                        {t("settings.name_label")}
                    </label>
                    <input
                        id="account-name-input"
                        type="text"
                        required
                        maxLength={50}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full bg-transparent border border-slate-300 px-4 py-3 rounded-sm focus:border-brand outline-none transition-colors mb-2"
                        data-testid="account-name-input"
                    />
                    <p className="text-xs text-slate-500 mb-6">{t("settings.name_hint")}</p>
                    <button type="submit" disabled={nameSaving} className="btn-primary" data-testid="account-name-save-btn">
                        <Save size={16} /> {t("settings.save")}
                    </button>
                </form>

                <div className="card-surface p-8 mb-8" data-testid="leaderboard-privacy-card">
                    <div className="mono-label mb-4 flex items-center gap-2">
                        <Trophy size={14} className="text-brand" /> {t("settings.leaderboard_privacy")}
                    </div>
                    <div className="flex items-center justify-between gap-4">
                        <p className="text-sm text-slate-600 max-w-md">{t("settings.leaderboard_opt_in_desc")}</p>
                        <Switch
                            checked={leaderboardOptIn}
                            onCheckedChange={handleToggleLeaderboard}
                            disabled={leaderboardSaving}
                            data-testid="leaderboard-opt-in-switch"
                        />
                    </div>
                </div>

                <form onSubmit={handleSave} className="card-surface p-8 mb-8" data-testid="settings-form">
                    <div className="mono-label mb-2 flex items-center gap-2">
                        <Bot size={14} className="text-brand" /> {t("settings.ai_identity")}
                    </div>
                    <label className="block text-sm text-slate-600 mb-2" htmlFor="ai-name-input">
                        {t("settings.ai_name_label")}
                    </label>
                    <input
                        id="ai-name-input"
                        type="text"
                        maxLength={30}
                        value={aiName}
                        onChange={(e) => setAiName(e.target.value)}
                        placeholder={t("settings.ai_name_placeholder")}
                        className="w-full bg-transparent border border-slate-300 px-4 py-3 rounded-sm focus:border-brand outline-none transition-colors mb-2"
                        data-testid="ai-name-input"
                    />
                    <p className="text-xs text-slate-500 mb-6">{t("settings.ai_name_hint")}</p>
                    <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save-btn">
                        <Save size={16} /> {t("settings.save")}
                    </button>
                </form>

                {isPaidPlan && (
                    <div className="card-surface p-8" data-testid="subscription-panel">
                        <div className="mono-label mb-4 flex items-center gap-2">
                            <CreditCard size={14} className="text-brand" /> {t("settings.billing_title")}
                        </div>
                        {subLoading && <p className="text-sm text-slate-500">{t("common.loading")}</p>}
                        {!subLoading && sub?.active && !sub.cancel_at_period_end && (
                            <>
                                <p className="text-sm text-slate-600 mb-4">
                                    {t("settings.sub_active_desc")}{" "}
                                    {sub.current_period_end && (
                                        <span className="font-medium text-slate-900">
                                            {new Date(sub.current_period_end * 1000).toLocaleDateString()}
                                        </span>
                                    )}
                                </p>
                                <button
                                    onClick={handleCancel}
                                    disabled={cancelling}
                                    className="btn-ghost text-sm"
                                    data-testid="cancel-subscription-btn"
                                >
                                    {t("settings.cancel_sub")}
                                </button>
                            </>
                        )}
                        {!subLoading && sub?.cancel_at_period_end && (
                            <div className="flex items-start gap-2 text-sm text-amber-600">
                                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                                <span>
                                    {t("settings.sub_cancelled_desc")}{" "}
                                    {sub.current_period_end && new Date(sub.current_period_end * 1000).toLocaleDateString()}
                                </span>
                            </div>
                        )}
                        {!subLoading && !sub?.active && (
                            <p className="text-sm text-slate-500">{t("settings.no_active_sub")}</p>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}
